import type * as React from 'react'
import { useState } from 'react'
import type { JSX } from 'react'

import { useMutation } from '@apollo/client/react'

import {
  Button,
  EmptyState,
  ExternalLinkIcon,
  Flex,
  MailIcon,
  Notice,
  toast,
} from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'

import { ACCOUNT_INITIATE_EMAIL_VERIFICATION } from '../../../Profile/components/Email'
import { SettingsPageSkeleton } from '../SettingsPageSkeleton'

import { getInboxUrl } from './getInboxUrl'

import styles from './EmailVerificationGate.module.css'

interface EmailVerificationGateProps {
  children: React.ReactNode
}

export function EmailVerificationGate({
  children,
}: EmailVerificationGateProps): JSX.Element | null {
  const { account, loading } = useAccount()
  const [sent, setSent] = useState(false)
  const [initiateEmailVerification] = useMutation(
    ACCOUNT_INITIATE_EMAIL_VERIFICATION,
    {
      onCompleted: (data) => {
        const result = data?.accountInitiateEmailVerification
        if (
          result?.__typename === 'AccountInitiateEmailVerificationResponse' &&
          result.success
        ) {
          toast.success(
            'An email has been sent to your inbox to verify your email address.',
          )
        } else if (
          result?.__typename === 'AccountInitiateEmailVerificationError'
        ) {
          setSent(false)
          toast.error(result.userFriendlyMessage)
        } else {
          setSent(false)
          toast.error(
            'An error occurred while sending the verification email. Please try again later.',
          )
        }
      },
      onError: () => {
        setSent(false)
        toast.error(
          'An error occurred while sending the verification email. Please try again later.',
        )
      },
    },
  )

  if (loading) {
    return <SettingsPageSkeleton />
  }

  if (account?.hasVerifiedEmail === true) {
    return <>{children}</>
  }

  const inboxProvider = getInboxUrl(account?.email)

  return (
    <Flex direction="column" gap="lg">
      <Notice variant="info">
        Refresh this page after verifying your email to continue.
      </Notice>
      <EmptyState size="xlarge" className={styles.container}>
        <EmptyState.Icon>
          <MailIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>
          Get started by verifying your email
        </EmptyState.Heading>
        <EmptyState.Description>
          We need to verify your email address to keep your account secure and
          make sure you get important updates. Check your inbox for the
          verification link, or resend the email:
        </EmptyState.Description>
        <EmptyState.Actions>
          <Button
            variant="secondary"
            disabled={sent}
            onClick={(): void => {
              setSent(true)
              void initiateEmailVerification()
            }}
          >
            <MailIcon />
            {sent ? 'Verification Email Sent' : 'Resend Verification Email'}
          </Button>
          {inboxProvider && (
            <Button
              variant="primary"
              as="a"
              href={inboxProvider.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open {inboxProvider.providerName}
              <ExternalLinkIcon />
            </Button>
          )}
        </EmptyState.Actions>
      </EmptyState>
    </Flex>
  )
}
